import { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CardActionArea,
  CardActions,
  CardContent,
  Chip,
  CircularProgress,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Pagination,
  Paper,
  Popper,
  Snackbar,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/DeleteOutlined';
import RefreshIcon from '@mui/icons-material/RefreshRounded';
import ReplayIcon from '@mui/icons-material/ReplayRounded';
import TuneIcon from '@mui/icons-material/TuneRounded';
import MoreVertIcon from '@mui/icons-material/MoreVertRounded';
import PlayArrowIcon from '@mui/icons-material/PlayArrowRounded';
import CollectionsIcon from '@mui/icons-material/CollectionsRounded';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutlineRounded';
import ContentCopyIcon from '@mui/icons-material/ContentCopyRounded';
import CheckIcon from '@mui/icons-material/CheckRounded';
import { useHistory, type HistoryItem } from '../hooks/useHistory';
import { useTranslation } from '../i18n';
import { PageHeader, Section } from '../components/Section';
import {
  MediaDialog,
  useResultUrls,
  featureLabel,
  canRemix,
  endpointRoute,
  remixStateFor,
  isImageEndpoint,
  statusColor,
  promptOf,
  originalPromptOf,
  type UrlEntry,
} from '../components/MediaDialog';


// ─── Hover Info Popper ────────────────────────────────────────────────────────

function CardInfoPopper({
  item,
  anchorEl,
  onMouseEnter,
  onMouseLeave,
  t,
}: {
  item: HistoryItem;
  anchorEl: HTMLElement | null;
  onMouseEnter: () => void;
  onMouseLeave: () => void;
  t: ReturnType<typeof useTranslation>;
}) {
  const [copied, setCopied] = useState(false);
  const prompt = promptOf(item);
  const originalPrompt = originalPromptOf(item);

  const handleCopy = async () => {
    if (!prompt) return;
    try {
      await navigator.clipboard.writeText(prompt);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {}
  };

  return (
    <Popper
      open={!!anchorEl}
      anchorEl={anchorEl}
      placement="top"
      modifiers={[{ name: 'offset', options: { offset: [0, 8] } }]}
      sx={{ zIndex: 1500 }}
    >
      <Paper
        elevation={4}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        sx={{
          p: 1.5,
          maxWidth: 280,
          border: '1px solid',
          borderColor: 'divider',
          pointerEvents: 'auto',
        }}
      >
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1, flexWrap: 'wrap', gap: 0.5 }}>
          <Typography variant="caption" sx={{ fontWeight: 600, wordBreak: 'break-all' }}>
            {t.history.detail.jobId}{' '}
            {item.external_request_id ?? `#${item.id}`}
          </Typography>
          <Chip
            size="small"
            label={t.history.status[item.job_status]}
            color={statusColor(item.job_status)}
            sx={{ height: 18, fontSize: 10 }}
          />
        </Stack>
        {item.batch_job_id && (
          <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 0.5, wordBreak: 'break-all' }}>
            {t.history.detail.batchJobId}{' '}
            <Box component="span" sx={{ fontFamily: 'ui-monospace, Menlo, monospace' }}>{item.batch_job_id}</Box>
            {item.batch_queue ? ` (${item.batch_queue})` : ''}
          </Typography>
        )}

        <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 0.5 }}>
          {item.model_name}
        </Typography>
        <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 1 }}>
          {new Date(item.created_at).toLocaleString()} · {Number(item.credits_used).toLocaleString()} {t.credits.balanceUnit}
        </Typography>

        {prompt && (
          <Box>
            {/* When enrichment ran, show the raw prompt as an extra labeled row
                above the enhanced prompt. */}
            {originalPrompt && (
              <Box sx={{ mb: 0.75 }}>
                <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, display: 'block' }}>
                  {t.history.detail.originalPrompt}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    color: 'text.secondary',
                    fontSize: 11,
                    display: '-webkit-box',
                    WebkitLineClamp: 3,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                  }}
                >
                  {originalPrompt}
                </Typography>
              </Box>
            )}
            {originalPrompt && (
              <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, display: 'block' }}>
                {t.history.detail.enhancedPrompt}
              </Typography>
            )}
            <Typography
              variant="body2"
              sx={{
                color: 'text.primary',
                fontSize: 11,
                display: '-webkit-box',
                WebkitLineClamp: 4,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
                mb: 0.5,
              }}
            >
              {prompt}
            </Typography>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Tooltip title={copied ? t.history.detail.copied : t.history.detail.copyPrompt} arrow>
                <IconButton size="small" onClick={handleCopy}>
                  {copied ? (
                    <CheckIcon sx={{ fontSize: 14, color: 'success.main' }} />
                  ) : (
                    <ContentCopyIcon sx={{ fontSize: 14 }} />
                  )}
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
        )}

        {item.job_status === 'failed' && item.error_message && (
          <Typography variant="caption" sx={{ color: 'error.main', display: 'block', mt: 0.5 }}>
            {item.error_message.slice(0, 120)}
          </Typography>
        )}
      </Paper>
    </Popper>
  );
}

// ─── History Card ─────────────────────────────────────────────────────────────

function HistoryCard({
  item,
  entry,
  selected,
  t,
  rerunning,
  onOpen,
  onDelete,
  onRerun,
  onRemix,
}: {
  item: HistoryItem;
  entry?: UrlEntry;
  selected: boolean;
  t: ReturnType<typeof useTranslation>;
  rerunning: boolean;
  onOpen: () => void;
  onDelete: () => void;
  onRerun: () => void;
  onRemix: () => void;
}) {
  const firstUrl = entry?.urls[0];
  const imageCount = entry?.urls.length ?? 0;
  const isImage = isImageEndpoint(item.endpoint);
  const ready = item.job_status === 'completed' && !!firstUrl;
  const canRetry = !!item.metadata?.input;
  const hasRemixRoute = canRemix(item);
  const prompt = promptOf(item);

  // Tooltip hover: wraps thumbnail + content area
  const [tipAnchor, setTipAnchor] = useState<HTMLElement | null>(null);
  const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const openTip = (el: HTMLElement) => {
    if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
    setTipAnchor(el);
  };
  const scheduleTipClose = (delay = 200) => {
    if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
    closeTimerRef.current = setTimeout(() => setTipAnchor(null), delay);
  };
  const cancelTipClose = () => {
    if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
  };

  // Actions menu (Rerun / Remix)
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);

  return (
    <Card
      sx={{
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        outline: selected ? '2px solid' : 'none',
        outlineColor: 'primary.main',
      }}
    >
      {/* Hover zone: thumbnail + content text — triggers both tooltip and click-to-open */}
      <Box
        onMouseEnter={(e) => openTip(e.currentTarget)}
        onMouseLeave={() => scheduleTipClose()}
        sx={{ cursor: ready ? 'pointer' : 'default' }}
        onClick={ready ? onOpen : undefined}
      >
        {/* Thumbnail — CardActionArea provides the ripple; click bubbles up to the Box */}
        <CardActionArea
          component="div"
          disabled={!ready}
          sx={{ cursor: 'inherit' }}
        >
          <Box
            sx={{
              position: 'relative',
              aspectRatio: '16 / 9',
              bgcolor: 'black',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {ready ? (
              isImage ? (
                <>
                  <Box
                    component="img"
                    src={firstUrl}
                    alt={prompt ?? 'result'}
                    loading="lazy"
                    sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                  />
                  {imageCount > 1 && (
                    <Chip
                      size="small"
                      icon={<CollectionsIcon sx={{ fontSize: 13, color: 'common.white !important' }} />}
                      label={imageCount}
                      sx={{
                        position: 'absolute',
                        top: 6,
                        right: 6,
                        height: 20,
                        fontSize: 11,
                        fontWeight: 600,
                        bgcolor: 'rgba(0,0,0,0.65)',
                        color: 'common.white',
                        '& .MuiChip-icon': { ml: '4px' },
                      }}
                    />
                  )}
                </>
              ) : (
                <>
                  <Box
                    component="video"
                    src={entry?.thumbnailUrl ? undefined : `${firstUrl}#t=0.1`}
                    poster={entry?.thumbnailUrl}
                    muted
                    preload={entry?.thumbnailUrl ? 'none' : 'metadata'}
                    playsInline
                    sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                  />
                  <Box
                    sx={{
                      position: 'absolute',
                      inset: 0,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: 'common.white',
                    }}
                  >
                    <PlayArrowIcon sx={{ fontSize: 56, filter: 'drop-shadow(0 1px 3px rgba(0,0,0,.6))' }} />
                  </Box>
                </>
              )
            ) : item.job_status === 'failed' ? (
              <ErrorOutlineIcon sx={{ fontSize: 40, color: 'error.main' }} />
            ) : (
              <CircularProgress size={28} sx={{ color: 'grey.500' }} />
            )}
          </Box>
        </CardActionArea>

        {/* Card text content — also participates in hover and click */}
        <CardContent sx={{ flex: 1, pb: 1 }}>
          <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mb: 1, flexWrap: 'wrap', gap: 0.5 }}>
            <Chip size="small" label={featureLabel(item.endpoint, t)} />
            <Chip
              size="small"
              label={t.history.status[item.job_status]}
              color={statusColor(item.job_status)}
            />
            {(item.promo_credits_used ?? 0) > 0 && (
              <Chip size="small" variant="outlined" color="secondary" label="Promotional" />
            )}
          </Stack>
          <Typography variant="body2" sx={{ fontFamily: 'ui-monospace, Menlo, monospace', fontSize: 12 }}>
            {item.model_name}
          </Typography>
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            {new Date(item.created_at).toLocaleString()} · {Number(item.credits_used).toLocaleString()}{' '}
            {t.history.table.credits.toLowerCase()}
          </Typography>
          {prompt && (
            <Typography
              variant="body2"
              sx={{
                mt: 0.75,
                color: 'text.secondary',
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
              }}
            >
              {prompt}
            </Typography>
          )}
        </CardContent>
      </Box>

      {/* Hover info tooltip (Popper) */}
      <CardInfoPopper
        item={item}
        anchorEl={tipAnchor}
        onMouseEnter={cancelTipClose}
        onMouseLeave={() => scheduleTipClose(150)}
        t={t}
      />

      {/* Actions row — not part of the hover/click zone */}
      <CardActions sx={{ justifyContent: 'flex-end', pt: 0, gap: 0 }}>
        {(canRetry || hasRemixRoute) && (
          <>
            <Tooltip title={rerunning ? t.history.detail.retrying : t.history.detail.rerun} arrow>
              <span>
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    setMenuAnchor(e.currentTarget);
                  }}
                  disabled={rerunning}
                >
                  {rerunning ? <CircularProgress size={16} /> : <MoreVertIcon fontSize="small" />}
                </IconButton>
              </span>
            </Tooltip>

            <Menu
              anchorEl={menuAnchor}
              open={!!menuAnchor}
              onClose={() => setMenuAnchor(null)}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
              {canRetry && (
                <MenuItem
                  onClick={() => {
                    setMenuAnchor(null);
                    onRerun();
                  }}
                >
                  <ListItemIcon><ReplayIcon fontSize="small" /></ListItemIcon>
                  <ListItemText
                    primary={t.history.detail.rerun}
                    secondary={t.history.detail.rerunTooltip}
                    secondaryTypographyProps={{ sx: { fontSize: 11, maxWidth: 220, whiteSpace: 'normal' } }}
                  />
                </MenuItem>
              )}
              {hasRemixRoute && (
                <MenuItem
                  onClick={() => {
                    setMenuAnchor(null);
                    onRemix();
                  }}
                >
                  <ListItemIcon><TuneIcon fontSize="small" /></ListItemIcon>
                  <ListItemText
                    primary={t.history.detail.remix}
                    secondary={t.history.detail.remixTooltip}
                    secondaryTypographyProps={{ sx: { fontSize: 11, maxWidth: 220, whiteSpace: 'normal' } }}
                  />
                </MenuItem>
              )}
            </Menu>
          </>
        )}

        <IconButton
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            onDelete();
          }}
        >
          <DeleteIcon fontSize="small" />
        </IconButton>
      </CardActions>
    </Card>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function GenerationHistory() {
  const t = useTranslation();
  const navigate = useNavigate();
  const { items, page, totalPages, setPage, loading, error, refresh, remove, presignedUrl, retry } =
    useHistory({ pollMs: 5_000 });
  const urlsMap = useResultUrls(items, presignedUrl);

  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [retryingId, setRetryingId] = useState<number | null>(null);
  const [snack, setSnack] = useState<{ kind: 'success' | 'error'; message: string } | null>(null);

  const selectedItem = selectedId != null ? items.find((it) => it.id === selectedId) ?? null : null;
  const selectedEntry = selectedId != null ? urlsMap[selectedId] : undefined;
  const selectedUrls = selectedEntry?.urls ?? [];

  const handleRerun = async (item: HistoryItem) => {
    setRetryingId(item.id);
    try {
      await retry(item.id);
      setSnack({ kind: 'success', message: t.history.detail.retrySuccess });
      void refresh();
    } catch (err) {
      const e = err as Error & { code?: string };
      setSnack({
        kind: 'error',
        message: e.code === 'inputs_expired' ? t.history.detail.retryInputsExpired : e.message || 'retry failed',
      });
    } finally {
      setRetryingId(null);
    }
  };

  const handleRemix = (item: HistoryItem) => {
    const route = endpointRoute(item.endpoint);
    if (!route) return;
    navigate(route, { state: remixStateFor(item) });
  };

  return (
    <Box>
      <PageHeader title={t.history.title} description={t.history.description} />

      {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

      <Section
        title="Generations"
        action={
          <Button size="small" startIcon={<RefreshIcon />} onClick={() => void refresh()}>
            Refresh
          </Button>
        }
      >
        {loading && items.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 8 }}>
            <CircularProgress size={24} />
          </Box>
        ) : items.length === 0 ? (
          <Typography variant="body2" sx={{ color: 'text.secondary', py: 6, textAlign: 'center' }}>
            {t.history.empty}
          </Typography>
        ) : (
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: '1fr',
                sm: 'repeat(2, 1fr)',
                md: 'repeat(3, 1fr)',
                lg: 'repeat(5, 1fr)',
              },
              gap: 2,
            }}
          >
            {items.map((it) => (
              <HistoryCard
                key={it.id}
                item={it}
                entry={urlsMap[it.id]}
                selected={selectedId === it.id}
                t={t}
                rerunning={retryingId === it.id}
                onOpen={() => setSelectedId(it.id)}
                onDelete={() => {
                  if (confirm(t.history.deleteConfirm)) void remove(it.id);
                }}
                onRerun={() => void handleRerun(it)}
                onRemix={() => handleRemix(it)}
              />
            ))}
          </Box>
        )}

        {totalPages > 1 && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
            <Pagination
              count={totalPages}
              page={page + 1}
              onChange={(_, p) => setPage(p - 1)}
              color="primary"
            />
          </Box>
        )}
      </Section>

      {/* Floating media dialog */}
      <MediaDialog
        item={selectedItem}
        urls={selectedUrls}
        onClose={() => setSelectedId(null)}
      />

      <Snackbar
        open={!!snack}
        autoHideDuration={6000}
        onClose={() => setSnack(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        {snack ? (
          <Alert severity={snack.kind} onClose={() => setSnack(null)} variant="filled">
            {snack.message}
          </Alert>
        ) : undefined}
      </Snackbar>
    </Box>
  );
}
